import { Injectable } from '@angular/core';
import { ChartConfiguration, ChartData, ChartOptions } from 'chart.js';
import { Observable, of } from 'rxjs';

import { ApiService } from './api.service';
import { TrendChartDto, TrendPoint } from '../models/trend-chart.model';
import { computeThresholdDate } from '../../utils/date-utils';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  constructor(private apiService: ApiService) {}

  getGainsChartData(): Observable<any> {
    return of({
      labels: ['T1 2024', 'T2 2024', 'T3 2024', 'T4 2024', 'T1 2025'],
      datasets: [
        {
          label: 'Gains réalisés (€)',
          data: [145000, 220000, 280000, 300000, 367500],
          backgroundColor: '#3b82f6',
          barPercentage: 0.6,
          categoryPercentage: 0.7,
        },
      ],
    });
  }

  getGainsChartOptions(): ChartConfiguration['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value: any) {
              return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 0,
              }).format(Number(value));
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const value = context.raw as number;
              return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
              }).format(value);
            },
          },
        },
      },
    };
  }

  getPieChartOptions(): any['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || '';
              const value = context.raw as number;
              const total = (
                context.chart.data.datasets[0].data as number[]
              ).reduce((a, b) => Number(a) + Number(b), 0);
              const percentage = Math.round((value / total) * 100);
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      },
    };
  }

  public getChartData(
    id: string,
    periodDays: string,
    interpolationInMinutes: number,
  ): Observable<TrendPoint[]> {
    const options = {
      queryParams: {
        ruleId: id.toString(),
        startDate: computeThresholdDate(periodDays).toISOString(),
        endDate: new Date().toISOString(),
        interpolationInMinutes: interpolationInMinutes,
      },
    };
    return this.apiService.get<TrendPoint[]>(
      `/RuleResult/GetChartData`,
      options,
    );
  }

  public getHealthIndicatorChartData(ruleId: string): Observable<TrendPoint[]> {
    return this.apiService.get<TrendPoint[]>(
      `/RuleResult/GetHealthIndicatorChartData`,
      { queryParams: { ruleId } },
    );
  }
}
